import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnector from './WalletConnector';
import { Clock, BarChart2, Box, Power, DollarSign, Settings, Users, Zap, RefreshCcw, Star } from 'lucide-react';
import './AdminPanel.css';

// Contract ABIs (simplified for this example)
const GrichInvABI = [
  "function addToRoyalty(uint256 _id) external",
  "function distributeRoyalty() external",
  "function setPrice(uint256 _amt) external",
  "function setRefIncomeLevelRatios(uint256 _level, uint256 _fee) external",
  "function setMinDeposit(uint256 minDep) external",
  "function buy(address _user, uint256 _ref, uint256 _amount, bool _useTopUpWallet) external payable",
  "function royaltyUsers() external view returns (uint256[] memory)",
  "function price() external view returns (uint256)",
  "function getAllUsers() external view returns (tuple(address acct, uint256 ref, uint256 upline, uint256 id, uint256 totalDepositUSDT, uint256 totalTokensClaimed, uint256 refIncomeUSDT, uint256 currentRoyaltyIncomeUSDT, uint256 royaltyIncomeUSDT, uint256 refCount, uint256 totalMatrixTeam)[] memory)",
  "function levelIncomeRatios(uint256) external view returns (uint256)",
  "function minDeposit() external view returns (uint256)",
  "function royaltyPool() external view returns (uint256)"
];

const TokenSwapABI = [
  "function changePrice(uint256 _price) external",
  "function toggleBnbSwaps(bool _enabled) external",
  "function updateBnbPrice(uint256 _bnbPrice) external",
  "function bnbSwapsEnabled() external view returns (bool)",
  "function price() external view returns (uint256)",
  "function lastBnbPrice() external view returns (uint256)"
];

// Main component
const AdminPanel = () => {
  // Wallet connection state
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  
  // Contract instances
  const [grichInvContract, setGrichInvContract] = useState(null);
  const [swapContract, setSwapContract] = useState(null);
  
  // Contract states
  const [grichPrice, setGrichPrice] = useState('0');
  const [swapPrice, setSwapPrice] = useState('0');
  const [minDeposit, setMinDeposit] = useState('0');
  const [levelRatios, setLevelRatios] = useState(Array(6).fill('0'));
  const [bnbSwapsEnabled, setBnbSwapsEnabled] = useState(false);
  const [bnbPrice, setBnbPrice] = useState('0');
  const [royaltyPoolAmount, setRoyaltyPoolAmount] = useState('0');
  const [royaltyUserCount, setRoyaltyUserCount] = useState(0);
  
  // UI states
  const [activeTab, setActiveTab] = useState('grich');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  
  // Form states
  const [newGrichPrice, setNewGrichPrice] = useState('');
  const [newSwapPrice, setNewSwapPrice] = useState('');
  const [newBnbPrice, setNewBnbPrice] = useState('');
  const [newMinDeposit, setNewMinDeposit] = useState('');
  const [levelToUpdate, setLevelToUpdate] = useState(0);
  const [newLevelRatio, setNewLevelRatio] = useState('');
  const [addRoyaltyId, setAddRoyaltyId] = useState('');
  const [buyUserAddress, setBuyUserAddress] = useState('');
  const [buyRefId, setBuyRefId] = useState('');
  const [buyAmount, setBuyAmount] = useState('');
  const [buyUseTopUp, setBuyUseTopUp] = useState(false);

  // Contract addresses - Replace with your actual contract addresses
  const grichInvAddress = "0x6aEfAA7798eF8662eB555E874930499a3A45DC7c"; // Replace with real address
  const swapContractAddress = "0xd0d7ca20E1CD1a167CCb99610B191744DC44216C"; // Replace with real address

  // Handle wallet connection
  const handleConnect = (address, walletProvider, walletType) => {
    try {
      setAccount(address);
      setProvider(walletProvider);
      setSigner(walletProvider.getSigner());
      
      // Log connection event
      addTransaction({
        type: 'connection',
        message: `Connected with ${walletType}`,
        timestamp: new Date().toISOString()
      });
      
      // Show notification
      showNotification('Wallet connected successfully!', 'success');
    } catch (error) {
      console.error('Connection error:', error);
      showNotification('Failed to connect wallet: ' + error.message, 'error');
    }
  };

  // Initialize contracts after connecting
  useEffect(() => {
    if (signer) {
      try {
        const grichContract = new ethers.Contract(grichInvAddress, GrichInvABI, signer);
        const tokenSwapContract = new ethers.Contract(swapContractAddress, TokenSwapABI, signer);
        
        setGrichInvContract(grichContract);
        setSwapContract(tokenSwapContract);
        
        // Load contract data
        fetchContractData(grichContract, tokenSwapContract);
      } catch (error) {
        console.error('Contract initialization error:', error);
        showNotification('Failed to initialize contracts', 'error');
      }
    }
  }, [signer]);

  // Fetch contract data
  const fetchContractData = async (grichContract, tokenSwapContract) => {
    try {
      setIsLoading(true);
      
      // Fetch Grich contract data
      const price = await grichContract.price();
      setGrichPrice(ethers.utils.formatUnits(price, 18));
      
      const minDep = await grichContract.minDeposit();
      setMinDeposit(ethers.utils.formatUnits(minDep, 18));
      
      // Fetch level ratios
      const ratios = [];
      for (let i = 0; i < 6; i++) {
        const ratio = await grichContract.levelIncomeRatios(i);
        ratios.push(ratio.toString());
      }
      setLevelRatios(ratios);
      
      // Fetch royalty pool data
      const royaltyPool = await grichContract.royaltyPool();
      setRoyaltyPoolAmount(ethers.utils.formatUnits(royaltyPool, 18));
      
      // Fetch TokenSwap contract data
      const swapPriceVal = await tokenSwapContract.price();
      setSwapPrice(ethers.utils.formatUnits(swapPriceVal, 18));
      
      const bnbSwaps = await tokenSwapContract.bnbSwapsEnabled();
      setBnbSwapsEnabled(bnbSwaps);
      
      const lastBnbPrice = await tokenSwapContract.lastBnbPrice();
      setBnbPrice(ethers.utils.formatUnits(lastBnbPrice, 18));
      
      // Get royalty users count (simplified)
      try {
        const royaltyUsers = await grichContract.royaltyUsers();
        setRoyaltyUserCount(royaltyUsers.length);
      } catch (error) {
        console.error("Failed to fetch royalty users:", error);
        setRoyaltyUserCount(0);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching contract data:', error);
      showNotification('Failed to fetch contract data', 'error');
      setIsLoading(false);
    }
  };

  // Refresh all data
  const refreshData = () => {
    if (grichInvContract && swapContract) {
      fetchContractData(grichInvContract, swapContract);
      showNotification('Data refreshed successfully!', 'success');
    }
  };

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Add transaction to history
  const addTransaction = (tx) => {
    setTransactions(prev => [tx, ...prev.slice(0, 9)]);
  };

  // Handle Grich Contract Functions
  const handleSetGrichPrice = async () => {
    try {
      if (!newGrichPrice) return;
      setIsLoading(true);
      
      const priceInWei = ethers.utils.parseUnits(newGrichPrice, 18);
      const tx = await grichInvContract.setPrice(priceInWei);
      await tx.wait();
      
      // Update state and add to transaction history
      setGrichPrice(newGrichPrice);
      setNewGrichPrice('');
      addTransaction({
        type: 'grich',
        message: `Set price to ${newGrichPrice}`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification('Price updated successfully!', 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error setting price:', error);
      showNotification('Failed to set price: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleSetMinDeposit = async () => {
    try {
      if (!newMinDeposit) return;
      setIsLoading(true);
      
      const depositInWei = ethers.utils.parseUnits(newMinDeposit, 18);
      const tx = await grichInvContract.setMinDeposit(depositInWei);
      await tx.wait();
      
      // Update state and add to transaction history
      setMinDeposit(newMinDeposit);
      setNewMinDeposit('');
      addTransaction({
        type: 'grich',
        message: `Set minimum deposit to ${newMinDeposit}`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification('Minimum deposit updated successfully!', 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error setting minimum deposit:', error);
      showNotification('Failed to set minimum deposit: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleSetLevelRatio = async () => {
    try {
      if (levelToUpdate < 0 || levelToUpdate > 5 || !newLevelRatio) return;
      setIsLoading(true);
      
      const tx = await grichInvContract.setRefIncomeLevelRatios(levelToUpdate, newLevelRatio);
      await tx.wait();
      
      // Update state and add to transaction history
      const updatedRatios = [...levelRatios];
      updatedRatios[levelToUpdate] = newLevelRatio;
      setLevelRatios(updatedRatios);
      
      setNewLevelRatio('');
      addTransaction({
        type: 'grich',
        message: `Set level ${levelToUpdate} ratio to ${newLevelRatio}%`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification(`Level ${levelToUpdate} ratio updated successfully!`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error setting level ratio:', error);
      showNotification('Failed to set level ratio: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleAddToRoyalty = async () => {
    try {
      if (!addRoyaltyId) return;
      setIsLoading(true);
      
      const tx = await grichInvContract.addToRoyalty(addRoyaltyId);
      await tx.wait();
      
      // Update state and add to transaction history
      setAddRoyaltyId('');
      addTransaction({
        type: 'grich',
        message: `Added user ID ${addRoyaltyId} to royalty pool`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      // Refresh royalty data
      const royaltyPool = await grichInvContract.royaltyPool();
      setRoyaltyPoolAmount(ethers.utils.formatUnits(royaltyPool, 18));
      
      try {
        const royaltyUsers = await grichInvContract.royaltyUsers();
        setRoyaltyUserCount(royaltyUsers.length);
      } catch (error) {
        console.error("Error fetching royalty users:", error);
      }
      
      showNotification(`User added to royalty pool successfully!`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error adding to royalty:', error);
      showNotification('Failed to add to royalty: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleDistributeRoyalty = async () => {
    try {
      setIsLoading(true);
      
      const tx = await grichInvContract.distributeRoyalty();
      await tx.wait();
      
      // Update state and add to transaction history
      addTransaction({
        type: 'grich',
        message: `Distributed royalty pool of ${royaltyPoolAmount} USDT`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      // Refresh royalty data
      const royaltyPool = await grichInvContract.royaltyPool();
      setRoyaltyPoolAmount(ethers.utils.formatUnits(royaltyPool, 18));
      
      showNotification(`Royalty distributed successfully!`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error distributing royalty:', error);
      showNotification('Failed to distribute royalty: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleBuy = async () => {
    try {
      if (!buyUserAddress || !buyRefId || !buyAmount) return;
      setIsLoading(true);
      
      const amountInWei = ethers.utils.parseUnits(buyAmount, 18);
      const tx = await grichInvContract.buy(buyUserAddress, buyRefId, amountInWei, buyUseTopUp);
      await tx.wait();
      
      // Update state and add to transaction history
      setBuyUserAddress('');
      setBuyRefId('');
      setBuyAmount('');
      setBuyUseTopUp(false);
      
      addTransaction({
        type: 'grich',
        message: `Buy for ${buyUserAddress} with ref ${buyRefId} amount ${buyAmount} USDT`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification(`Buy transaction completed successfully!`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error executing buy function:', error);
      showNotification('Failed to execute buy: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  // Handle TokenSwap Contract Functions
  const handleSetSwapPrice = async () => {
    try {
      if (!newSwapPrice) return;
      setIsLoading(true);
      
      const priceInWei = ethers.utils.parseUnits(newSwapPrice, 18);
      const tx = await swapContract.changePrice(priceInWei);
      await tx.wait();
      
      // Update state and add to transaction history
      setSwapPrice(newSwapPrice);
      setNewSwapPrice('');
      addTransaction({
        type: 'swap',
        message: `Set swap price to ${newSwapPrice}`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification('Swap price updated successfully!', 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error setting swap price:', error);
      showNotification('Failed to set swap price: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleToggleBnbSwaps = async () => {
    try {
      setIsLoading(true);
      
      const tx = await swapContract.toggleBnbSwaps(!bnbSwapsEnabled);
      await tx.wait();
      
      // Update state and add to transaction history
      setBnbSwapsEnabled(!bnbSwapsEnabled);
      addTransaction({
        type: 'swap',
        message: `${!bnbSwapsEnabled ? 'Enabled' : 'Disabled'} BNB swaps`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification(`BNB swaps ${!bnbSwapsEnabled ? 'enabled' : 'disabled'} successfully!`, 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error toggling BNB swaps:', error);
      showNotification('Failed to toggle BNB swaps: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  const handleUpdateBnbPrice = async () => {
    try {
      if (!newBnbPrice) return;
      setIsLoading(true);
      
      const priceInWei = ethers.utils.parseUnits(newBnbPrice, 18);
      const tx = await swapContract.updateBnbPrice(priceInWei);
      await tx.wait();
      
      // Update state and add to transaction history
      setBnbPrice(newBnbPrice);
      setNewBnbPrice('');
      addTransaction({
        type: 'swap',
        message: `Updated BNB price to ${newBnbPrice}`,
        timestamp: new Date().toISOString(),
        hash: tx.hash
      });
      
      showNotification('BNB price updated successfully!', 'success');
      setIsLoading(false);
    } catch (error) {
      console.error('Error updating BNB price:', error);
      showNotification('Failed to update BNB price: ' + error.message, 'error');
      setIsLoading(false);
    }
  };

  // Render the UI
  return (
    <div className="admin-container">
  {/* Header */}
  <div className="admin-header">
    <div className="admin-flex admin-items-center">
      <Zap className="admin-mr-2" size={24} color="#a78bfa" />
      <h1 className="text-xl font-bold">AI Admin Dashboard</h1>
    </div>
    <div>
      <WalletConnector 
        onConnect={handleConnect} 
        requiredChainId={204} // opBNB chain ID
        buttonClassName="admin-button-primary"
      />
    </div>
  </div>

  {/* Mobile Navigation - NEW */}
  <div className="admin-mobile-nav">
    <button 
      className={`admin-mobile-nav-button ${activeTab === 'grich' ? 'active' : ''}`}
      onClick={() => setActiveTab('grich')}
    >
      <span className="admin-flex admin-items-center admin-justify-center">
        <BarChart2 size={16} className="admin-mr-2" />
        Grich
      </span>
    </button>
    <button 
      className={`admin-mobile-nav-button ${activeTab === 'swap' ? 'active' : ''}`}
      onClick={() => setActiveTab('swap')}
    >
      <span className="admin-flex admin-items-center admin-justify-center">
        <Box size={16} className="admin-mr-2" />
        Swap
      </span>
    </button>
    <button 
      className={`admin-mobile-nav-button ${activeTab === 'transactions' ? 'active' : ''}`}
      onClick={() => setActiveTab('transactions')}
    >
      <span className="admin-flex admin-items-center admin-justify-center">
        <Clock size={16} className="admin-mr-2" />
        History
      </span>
    </button>
  </div>

      {/* Main content */}
      <div className="admin-main-content">
        {/* Sidebar */}
        <div className="admin-sidebar">
          <button 
            className={`admin-button-secondary admin-w-full admin-mb-2 admin-flex admin-items-center ${activeTab === 'grich' ? 'admin-bg-gray-600' : ''}`}
            onClick={() => setActiveTab('grich')}
          >
            <BarChart2 className="admin-mr-2" size={18} />
            Grich Investment
          </button>
          <button 
            className={`admin-button-secondary admin-w-full admin-mb-2 admin-flex admin-items-center ${activeTab === 'swap' ? 'admin-bg-gray-600' : ''}`}
            onClick={() => setActiveTab('swap')}
          >
            <Box className="admin-mr-2" size={18} />
            Token Swap
          </button>
          <button 
            className={`admin-button-secondary admin-w-full admin-mb-2 admin-flex admin-items-center ${activeTab === 'transactions' ? 'admin-bg-gray-600' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <Clock className="admin-mr-2" size={18} />
            Transactions
          </button>
          
          <div className="admin-mt-6 admin-p-4 admin-bg-gray-700 admin-rounded">
            <h3 className="admin-text-sm admin-font-medium admin-text-gray-300 admin-mb-2">Connected Account</h3>
            {account ? (
              <p className="admin-text-xs admin-break-all">{account}</p>
            ) : (
              <p className="admin-text-xs admin-text-gray-400">Not connected</p>
            )}
          </div>

          <button 
            className="admin-button-primary admin-w-full admin-mt-6 admin-flex admin-items-center admin-justify-center"
            onClick={refreshData}
            disabled={isLoading || !signer}
          >
            <RefreshCcw className="admin-mr-2" size={18} />
            Refresh Data
          </button>
        </div>

        {/* Content area */}
        <div className="admin-content-area">
          {/* Grich Investment Tab */}
          {activeTab === 'grich' && (
            <div>
              {/* Stats */}
              <div className="admin-stats-container">
                <div className="admin-stat-card">
                  <DollarSign className="admin-stat-icon" size={24} color="#10b981" />
                  <div>
                    <h3 className="admin-stat-title">Token Price</h3>
                    <p className="admin-stat-value">${grichPrice}</p>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <Settings className="admin-stat-icon" size={24} color="#3b82f6" />
                  <div>
                    <h3 className="admin-stat-title">Min Deposit</h3>
                    <p className="admin-stat-value">${minDeposit} USDT</p>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <Star className="admin-stat-icon" size={24} color="#eab308" />
                  <div>
                    <h3 className="admin-stat-title">Royalty Pool</h3>
                    <p className="admin-stat-value">${royaltyPoolAmount} USDT</p>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <Users className="admin-stat-icon" size={24} color="#a78bfa" />
                  <div>
                    <h3 className="admin-stat-title">Royalty Users</h3>
                    <p className="admin-stat-value">{royaltyUserCount}</p>
                  </div>
                </div>
              </div>

              {/* Price and Min Deposit */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <DollarSign className="admin-mr-2" size={20} />
                  Set Token Price & Min Deposit
                </h2>
                
                <div className="admin-grid">
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">New Token Price (in USD)</label>
                      <div className="admin-flex">
                        <input 
                          type="number" 
                          className="admin-input admin-flex-1"
                          placeholder="Enter new price" 
                          value={newGrichPrice}
                          onChange={(e) => setNewGrichPrice(e.target.value)}
                        />
                        <button 
                          className="admin-button-primary admin-ml-2"
                          onClick={handleSetGrichPrice}
                          disabled={isLoading || !signer || !newGrichPrice}
                        >
                          Set Price
                        </button>
                      </div>
                      <p className="admin-text-xs admin-text-gray-400 admin-mt-1">Current Price: ${grichPrice}</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">New Minimum Deposit (in USDT)</label>
                      <div className="admin-flex">
                        <input 
                          type="number" 
                          className="admin-input admin-flex-1"
                          placeholder="Enter min deposit" 
                          value={newMinDeposit}
                          onChange={(e) => setNewMinDeposit(e.target.value)}
                        />
                        <button 
                          className="admin-button-primary admin-ml-2"
                          onClick={handleSetMinDeposit}
                          disabled={isLoading || !signer || !newMinDeposit}
                        >
                          Set Min
                        </button>
                      </div>
                      <p className="admin-text-xs admin-text-gray-400 admin-mt-1">Current Min: {minDeposit} USDT</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Level Ratios */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <Settings className="admin-mr-2" size={20} />
                  Set Level Income Ratios
                </h2>
                
                <div className="admin-grid">
                  <div className="admin-flex admin-flex-wrap admin-gap-2 admin-mb-4">
                    {levelRatios.map((ratio, index) => (
                      <div key={index} className="admin-px-3 admin-py-2 admin-bg-gray-700 admin-rounded">
                        Level {index}: {ratio}%
                      </div>
                    ))}
                  </div>
                  
                  <div className="admin-flex admin-flex-wrap admin-gap-4">
                    <div className="admin-form-group">
                      <label className="admin-label">Level (0-5)</label>
                      <input 
                        type="number" 
                        min="0"
                        max="5"
                        className="admin-input"
                        placeholder="Level" 
                        value={levelToUpdate}
                        onChange={(e) => setLevelToUpdate(parseInt(e.target.value))}
                      />
                    </div>
                    
                    <div className="admin-form-group">
                      <label className="admin-label">New Ratio (%)</label>
                      <input 
                        type="number" 
                        className="admin-input"
                        placeholder="Ratio percent" 
                        value={newLevelRatio}
                        onChange={(e) => setNewLevelRatio(e.target.value)}
                      />
                    </div>
                    
                    <div className="admin-form-group">
                      <label className="admin-label">&nbsp;</label>
                      <button 
                        className="admin-button-primary"
                        onClick={handleSetLevelRatio}
                        disabled={isLoading || !signer || !newLevelRatio || levelToUpdate < 0 || levelToUpdate > 5}
                      >
                        Update Ratio
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Royalty Management */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <Star className="admin-mr-2" size={20} />
                  Royalty Management
                </h2>
                
                <div className="admin-grid">
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">Add User to Royalty Pool</label>
                      <div className="admin-flex">
                        <input 
                          type="number" 
                          className="admin-input admin-flex-1"
                          placeholder="Enter user ID" 
                          value={addRoyaltyId}
                          onChange={(e) => setAddRoyaltyId(e.target.value)}
                        />
                        <button 
                          className="admin-button-primary admin-ml-2"
                          onClick={handleAddToRoyalty}
                          disabled={isLoading || !signer || !addRoyaltyId}
                        >
                          Add User
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">Distribute Royalty Pool</label>
                      <div className="admin-flex admin-items-center">
                        <div className="admin-bg-gray-700 admin-px-4 admin-py-2 admin-rounded admin-flex-1">
                          <p className="admin-text-sm">Current Pool: {royaltyPoolAmount} USDT</p>
                          <p className="admin-text-xs admin-text-gray-400">Users: {royaltyUserCount}</p>
                        </div>
                        <button 
                          className="admin-button-success admin-ml-2"
                          onClick={handleDistributeRoyalty}
                          disabled={isLoading || !signer || parseFloat(royaltyPoolAmount) <= 0}
                        >
                          Distribute
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Buy Function */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <Box className="admin-mr-2" size={20} />
                  Execute Buy Function
                </h2>
                
                <div className="admin-grid">
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">User Address</label>
                      <input 
                        type="text" 
                        className="admin-input"
                        placeholder="0x..." 
                        value={buyUserAddress}
                        onChange={(e) => setBuyUserAddress(e.target.value)}
                      />
                    </div>
                    
                    <div className="admin-form-group">
                      <label className="admin-label">Referrer ID</label>
                      <input 
                        type="number" 
                        className="admin-input"
                        placeholder="Referrer ID" 
                        value={buyRefId}
                        onChange={(e) => setBuyRefId(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="admin-form-group">
                      <label className="admin-label">Amount (USDT)</label>
                      <input 
                        type="number" 
                        className="admin-input"
                        placeholder="Amount" 
                        value={buyAmount}
                        onChange={(e) => setBuyAmount(e.target.value)}
                      />
                    </div>
                    
                    <div className="admin-form-group">
                      <label className="admin-flex admin-items-center admin-mb-3">
                        <input 
                          type="checkbox"
                          className="admin-mr-2"
                          checked={buyUseTopUp}
                          onChange={(e) => setBuyUseTopUp(e.target.checked)}
                        />
                        <span className="admin-text-gray-300">Use TopUp Wallet</span>
                      </label>
                      
                      <button 
                        className="admin-button-success"
                        onClick={handleBuy}
                        disabled={isLoading || !signer || !buyUserAddress || !buyRefId || !buyAmount}
                      >
                        Execute Buy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Token Swap Tab */}
          {activeTab === 'swap' && (
            <div>
              {/* Stats */}
              <div className="admin-stats-container">
                <div className="admin-stat-card">
                  <DollarSign className="admin-stat-icon" size={24} color="#10b981" />
                  <div>
                    <h3 className="admin-stat-title">Swap Price</h3>
                    <p className="admin-stat-value">${swapPrice}</p>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <DollarSign className="admin-stat-icon" size={24} color="#eab308" />
                  <div>
                    <h3 className="admin-stat-title">BNB Price</h3>
                    <p className="admin-stat-value">${bnbPrice}</p>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <Power className="admin-stat-icon" size={24} color={bnbSwapsEnabled ? "#10b981" : "#ef4444"} />
                  <div>
                    <h3 className="admin-stat-title">BNB Swaps</h3>
                    <p className="admin-stat-value">{bnbSwapsEnabled ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
              </div>
              
              {/* Swap Price */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <DollarSign className="admin-mr-2" size={20} />
                  Set Swap Price
                </h2>
                
                <div className="admin-grid">
                  <div className="admin-form-group">
                    <label className="admin-label">New Swap Price (in USD)</label>
                    <div className="admin-flex">
                      <input 
                        type="number" 
                        className="admin-input admin-flex-1"
                        placeholder="Enter new price" 
                        value={newSwapPrice}
                        onChange={(e) => setNewSwapPrice(e.target.value)}
                      />
                      <button 
                        className="admin-button-primary admin-ml-2"
                        onClick={handleSetSwapPrice}
                        disabled={isLoading || !signer || !newSwapPrice}
                      >
                        Update Price
                      </button>
                    </div>
                    <p className="admin-text-xs admin-text-gray-400 admin-mt-1">Current Price: ${swapPrice}</p>
                  </div>
                </div>
              </div>
              
              {/* BNB Price */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <DollarSign className="admin-mr-2" size={20} />
                  Update BNB Price
                </h2>
                
                <div className="admin-grid">
                  <div className="admin-form-group">
                    <label className="admin-label">New BNB Price (in USD)</label>
                    <div className="admin-flex">
                      <input 
                        type="number" 
                        className="admin-input admin-flex-1"
                        placeholder="Enter BNB price" 
                        value={newBnbPrice}
                        onChange={(e) => setNewBnbPrice(e.target.value)}
                      />
                      <button 
                        className="admin-button-primary admin-ml-2"
                        onClick={handleUpdateBnbPrice}
                        disabled={isLoading || !signer || !newBnbPrice}
                      >
                        Update Price
                      </button>
                    </div>
                    <p className="admin-text-xs admin-text-gray-400 admin-mt-1">Current BNB Price: ${bnbPrice}</p>
                  </div>
                </div>
              </div>
              
              {/* Toggle BNB Swaps */}
              <div className="admin-card">
                <h2 className="admin-card-title">
                  <Power className="admin-mr-2" size={20} />
                  Toggle BNB Swaps
                </h2>
                
                <div className="admin-flex admin-items-center admin-justify-between">
                  <div>
                    <h3 className="admin-text-lg admin-font-medium">BNB Swaps: 
                      <span className={bnbSwapsEnabled ? 'admin-text-green-500' : 'admin-text-red-500'}>
                        {bnbSwapsEnabled ? ' Enabled' : ' Disabled'}
                      </span>
                    </h3>
                    <p className="admin-text-gray-400 admin-text-sm admin-mt-1">Toggle to enable or disable BNB swap functionality</p>
                  </div>
                  
                  <button 
                    className={bnbSwapsEnabled ? "admin-button-danger" : "admin-button-success"}
                    onClick={handleToggleBnbSwaps}
                    disabled={isLoading || !signer}
                  >
                    {bnbSwapsEnabled ? 'Disable Swaps' : 'Enable Swaps'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="admin-card">
              <h2 className="admin-card-title">
                <Clock className="admin-mr-2" size={20} />
                Recent Transactions
              </h2>
              
              {transactions.length > 0 ? (
                <div className="admin-table-container">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Message</th>
                        <th>Time</th>
                        <th>Hash</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`admin-badge ${
                              tx.type === 'grich' ? 'admin-bg-purple-900 admin-text-purple-200' :
                              tx.type === 'swap' ? 'admin-bg-blue-900 admin-text-blue-200' :
                              'admin-bg-gray-700 admin-text-gray-300'
                            }`}>
                              {tx.type}
                            </span>
                          </td>
                          <td>{tx.message}</td>
                          <td>
                            {new Date(tx.timestamp).toLocaleTimeString()}
                          </td>
                          <td>
                            {tx.hash ? (
                              <a 
                                href={`https://opbnbscan.com/tx/${tx.hash}`} 
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-text-purple-400 admin-hover-underline"
                              >
                                {tx.hash.slice(0, 8)}...{tx.hash.slice(-6)}
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="admin-p-8 admin-text-center admin-text-gray-400">
                  <p>No transactions yet. Connect your wallet and perform actions to see them here.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Notification */}
      {notification.show && (
        <div className={`admin-notification ${
          notification.type === 'success' ? 'admin-notification-success' :
          notification.type === 'error' ? 'admin-notification-error' :
          'admin-bg-blue-800 admin-border-l-4 admin-border-blue-500'
        }`}>
          <p>{notification.message}</p>
        </div>
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="admin-loading-overlay">
          <div className="admin-loading-container">
            <div className="admin-loading-spinner"></div>
            <p>Processing transaction...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;